import { useAuth0 } from "ct-auth"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import {
  getSession,
  getSessionGroup,
  getScenarios,
  getSessionGroups,
  getExercises,
  getOrg,
  getOrgs,
  getUserData,
  getIncidents,
  getIncidentView,
  getIncidentAuditView,
  getIncidentAuditForMember,
  getIncidentNotifications,
  fetchOrgIRPolicys,
  getOrgVariables,
  getCtfLeaderboard,
  getCtfPlayerReport,
  getCtfEventReport,
} from "./data.js"
import { fetchPeerScoreSessionGroupsForGroup, fetchRelatedHistoricalSessionGroupsForGroup } from "./gameSessionGroupUtils.js"
import { fetchRelatedHistoricalSessionsForSession } from "./gameSessionUtils.js"
import logger from "../Logger/index.js"

const pageLogger = logger.child({ module: "reactQueryHelpers" })

const EXERCISES_REFETCH_INTERVAL = Number(process.env.REACT_APP_EXERCISES_REFETCH_INTERVAL) || 30000

export const cacheKeyGens = Object.freeze({
  GROUP_DATA: ({ userId, groupName }) => ["GROUP_DATA", userId, groupName],
  GROUP_HISTORY_DATA: ({ userId, groupName }) => ["GROUP_HISTORY_DATA", userId, groupName],
  GROUP_PEER_DATA: ({ userId, groupName }) => ["GROUP_PEER_DATA", userId, groupName],
  SESSION_DATA: ({ userId, sessionId }) => ["SESSION_DATA", userId, sessionId],
  SESSION_HISTORY_DATA: ({ userId, sessionId }) => ["SESSION_HISTORY_DATA", userId, sessionId],
  HOME_SCENARIOS: ({ userId }) => ["HOME_SCENARIOS", userId],
  HOME_SESSION_GROUPS: ({ userId, filterParams }) => ["HOME_SESSION_GROUPS", userId, JSON.stringify(filterParams)],
  HOME_USER_ORG: ({ userId, orgId }) => ["HOME_USER_ORG", userId, orgId],
  // perf/lean-org-fetch: the heavy org variables (core_systems_profiles,
  // core_system_supporting_documents, ir_policys) that /getOrg no longer carries.
  // Cached independently of HOME_USER_ORG so Settings can source them on demand
  // without bloating the every-page org fetch.
  HOME_USER_ORG_VARIABLES: ({ userId, orgId }) => ["HOME_USER_ORG_VARIABLES", userId, orgId],
  HOME_ALL_ORGS: ({ userId }) => ["HOME_ALL_ORGS", userId],
  HOME_EXERCISES: ({ userId, filterParams }) => ["HOME_EXERCISES", userId, JSON.stringify(filterParams)],
  HOME_USER_DATA: ({ userId, emails }) => ["HOME_USER_DATA", userId, JSON.stringify([...emails].sort())],
  // Per-game user status - lazy loaded on demand
  HOME_GAME_USER_STATUS: ({ userId, gameId, emails }) => ["HOME_GAME_USER_STATUS", userId, gameId, JSON.stringify([...emails].sort())],
  CTF_LEADERBOARD: ({ userId, groupName }) => ["CTF_LEADERBOARD", userId, groupName],
  CTF_PLAYER_REPORT: ({ userId, name }) => ["CTF_PLAYER_REPORT", userId, name],
  CTF_EVENT_REPORT: ({ userId, groupName }) => ["CTF_EVENT_REPORT", userId, groupName],
  // Incidents (Task 22)
  INCIDENTS: ({ userId, limit, skip }) => ["INCIDENTS", userId, limit ?? null, skip ?? null],
  INCIDENT_VIEW: ({ userId, incidentKey }) => ["INCIDENT_VIEW", userId, incidentKey],
  INCIDENT_AUDIT: ({ userId, incidentKey, limit, beforeSeq }) => ["INCIDENT_AUDIT", userId, incidentKey, limit ?? null, beforeSeq ?? null],
  // M4.5: member-scoped audit feed. Distinct key from INCIDENT_AUDIT so the
  // redacted member payload never collides with the admin-only cache entry.
  INCIDENT_AUDIT_MEMBER: ({ userId, incidentKey, limit, beforeSeq }) => [
    "INCIDENT_AUDIT_MEMBER",
    userId,
    incidentKey,
    limit ?? null,
    beforeSeq ?? null,
  ],
  // M4.6: the caller's own incident notification feed for the header bell.
  // Scoped by userId only — one feed per signed-in user.
  INCIDENT_NOTIFICATIONS: ({ userId }) => ["INCIDENT_NOTIFICATIONS", userId],
  ORG_IR_POLICYS: ({ userId, orgId }) => ["ORG_IR_POLICYS", userId, orgId],
})

export const useQueries = Object.freeze({
  GROUP_DATA: ({ groupName }) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.GROUP_DATA({ userId: user.sub, groupName }),
      queryFn: async () => {
        if (user.sub) {
          const accessToken = await getAccessTokenSilently()
          return await getSessionGroup(accessToken, user.sub, { groupName })
        }
      },
      enabled: !!(user?.sub && groupName),
      staleTime: 30000,
    })
  },
  GROUP_HISTORY_DATA: ({ groupName, groupData }) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.GROUP_HISTORY_DATA({ userId: user.sub, groupName }),
      queryFn: async () => {
        if (user.sub) {
          const accessToken = await getAccessTokenSilently()
          return await fetchRelatedHistoricalSessionGroupsForGroup({
            accessToken,
            userId: user.sub,
            sessionGroup: groupData,
          })
        }
      },
      enabled: !!(groupData && groupName),
      staleTime: 30000,
    })
  },
  GROUP_PEER_DATA: ({ groupName, groupData }) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.GROUP_PEER_DATA({ userId: user.sub, groupName }),
      queryFn: async () => {
        if (user.sub) {
          const accessToken = await getAccessTokenSilently()
          return await fetchPeerScoreSessionGroupsForGroup({
            accessToken,
            userId: user.sub,
            sessionGroup: groupData,
          })
        }
      },
      enabled: !!(groupData && groupName),
      staleTime: 30000,
    })
  },
  SESSION_DATA: ({ sessionId }) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.SESSION_DATA({ userId: user.sub, sessionId }),
      queryFn: async () => {
        if (user.sub) {
          const accessToken = await getAccessTokenSilently()
          const { result: sessionData } = await getSession(accessToken, user.sub, {
            name: sessionId,
          })
          return sessionData
        }
      },
      enabled: !!(sessionId && user?.sub),
      staleTime: 30000,
    })
  },
  SESSION_HISTORY_DATA: ({ sessionData }) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.SESSION_HISTORY_DATA({ userId: user.sub, sessionId: sessionData?.name }),
      queryFn: async () => {
        if (user.sub) {
          const accessToken = await getAccessTokenSilently()
          return await fetchRelatedHistoricalSessionsForSession({
            accessToken,
            userId: user.sub,
            session: sessionData,
          })
        }
      },
      enabled: !!(sessionData && sessionData?.name),
      staleTime: 30000,
    })
  },
  HOME_SCENARIOS: ({ enabled = true }) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.HOME_SCENARIOS({ userId: user?.sub }),
      queryFn: async () => {
        if (user?.sub) {
          const accessToken = await getAccessTokenSilently()
          pageLogger.debug({ userId: user.sub }, "Fetching scenarios for home")
          return await getScenarios(accessToken, user.sub)
        }
      },
      enabled: !!(user?.sub && enabled),
      staleTime: 30000,
      refetchInterval: 30000,
      refetchIntervalInBackground: false,
    })
  },
  HOME_SESSION_GROUPS: ({ filterParams, enabled = true }) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.HOME_SESSION_GROUPS({ userId: user?.sub, filterParams }),
      queryFn: async () => {
        if (user?.sub) {
          const accessToken = await getAccessTokenSilently()
          pageLogger.debug({ userId: user.sub, filterParams }, "Fetching session groups for home")
          return await getSessionGroups(accessToken, user.sub, filterParams)
        }
      },
      enabled: !!(user?.sub && enabled),
      staleTime: 30000,
      refetchInterval: 30000,
      refetchIntervalInBackground: false,
    })
  },
  HOME_EXERCISES: ({ filterParams, enabled = true }) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.HOME_EXERCISES({ userId: user?.sub, filterParams }),
      queryFn: async () => {
        if (user?.sub) {
          const accessToken = await getAccessTokenSilently()
          pageLogger.debug({ userId: user.sub, filterParams }, "Fetching exercises for home")
          return await getExercises(accessToken, user.sub, filterParams)
        }
      },
      enabled: !!(user?.sub && enabled),
      staleTime: EXERCISES_REFETCH_INTERVAL,
      refetchInterval: EXERCISES_REFETCH_INTERVAL,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
    })
  },
  // fresh: always refetch when the caller mounts (dialogs that decide from the org's current
  // settings, e.g. a partner's grant list, must not act on a copy cached minutes ago).
  HOME_USER_ORG: ({ orgId, enabled = true, fresh = false }) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.HOME_USER_ORG({ userId: user?.sub, orgId }),
      queryFn: async () => {
        if (user?.sub && orgId) {
          const accessToken = await getAccessTokenSilently()
          pageLogger.debug({ userId: user.sub, orgId }, "Fetching user org for home")
          return await getOrg(accessToken, user.sub, { id: orgId })
        }
      },
      enabled: !!(user?.sub && orgId && enabled),
      // Perf (perf/lean-org-fetch): the org doc is large (~3.5 MB for big orgs) and
      // was being re-fetched every 30 s on every page. It only changes on explicit
      // org edits, which already call invalidateOrgCache(). Stop polling; refresh on
      // remount past the stale window (real edits invalidate immediately).
      staleTime: fresh ? 0 : 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      ...(fresh ? { refetchOnMount: "always" } : {}),
    })
  },
  // perf/lean-org-fetch: the FULL org variables (heavy keys included) fetched from the
  // dedicated /org/:id/variables endpoint. Settings derives its Core-Systems profiles,
  // supporting documents and IR-plan runbooks from this instead of the (now trimmed)
  // HOME_USER_ORG doc. Cached under its own key so a Settings save invalidates it
  // independently of the every-page org fetch.
  HOME_USER_ORG_VARIABLES: ({ orgId, enabled = true, refetchIntervalMs = undefined }) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.HOME_USER_ORG_VARIABLES({ userId: user?.sub, orgId }),
      queryFn: async () => {
        if (user?.sub && orgId) {
          const accessToken = await getAccessTokenSilently()
          pageLogger.debug({ userId: user.sub, orgId }, "Fetching org variables (heavy keys) for settings")
          const orgVars = await getOrgVariables(accessToken, user.sub, { id: orgId })
          return orgVars?.variables ?? {}
        }
        return {}
      },
      enabled: !!(user?.sub && orgId && enabled),
      // Mirror HOME_USER_ORG's freshness policy — real edits invalidate immediately.
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      // Off by default. Settings passes a short interval ONLY while an uploaded runbook is
      // awaiting the cron's text extraction, so the "Processing…" card self-heals like it did
      // under the old always-on 30s org poll — without reinstating that poll for everyone.
      refetchInterval: refetchIntervalMs,
      refetchIntervalInBackground: false,
    })
  },
  HOME_ALL_ORGS: ({ enabled = true }) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.HOME_ALL_ORGS({ userId: user?.sub }),
      queryFn: async () => {
        if (user?.sub) {
          const accessToken = await getAccessTokenSilently()
          pageLogger.debug({ userId: user.sub }, "Fetching all orgs for home")
          return await getOrgs(accessToken, user.sub)
        }
      },
      enabled: !!(user?.sub && enabled),
      staleTime: 30000,
      refetchInterval: 30000,
      refetchIntervalInBackground: false,
    })
  },
  HOME_USER_DATA: ({ emails, enabled = true }) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.HOME_USER_DATA({ userId: user?.sub, emails }),
      queryFn: async () => {
        if (user?.sub && emails && emails.length > 0) {
          const accessToken = await getAccessTokenSilently()
          pageLogger.debug({ userId: user.sub, emailCount: emails.length }, "Fetching user data for home")
          return await getUserData(accessToken, user.sub, { emails })
        }
      },
      enabled: !!(user?.sub && emails && emails.length > 0 && enabled),
      staleTime: 30000,
      refetchInterval: 30000,
      refetchIntervalInBackground: false,
    })
  },
  // CTF reports spec §4: read-only composed reports (no LLM on read; the server caches them).
  CTF_PLAYER_REPORT: ({ name, enabled = true }) => {
    const { getAccessTokenSilently, user } = useAuth0()
    const userId = user?.sub
    return useQuery({
      queryKey: cacheKeyGens.CTF_PLAYER_REPORT({ userId, name }),
      queryFn: async () => {
        if (!userId) return null
        const accessToken = await getAccessTokenSilently()
        return await getCtfPlayerReport(accessToken, userId, { name })
      },
      enabled: !!(userId && name && enabled),
      staleTime: 60000,
      // A self-paced player's report changes as others finish (ranks) and unlocks at close.
      refetchInterval: (query) => (query.state.data && query.state.data.closed === false ? 60000 : false),
      refetchIntervalInBackground: false,
      retry: (failureCount, error) => failureCount < 2 && !(error?.status >= 400 && error?.status < 500),
    })
  },
  CTF_EVENT_REPORT: ({ groupName, enabled = true }) => {
    const { getAccessTokenSilently, user } = useAuth0()
    const userId = user?.sub
    return useQuery({
      queryKey: cacheKeyGens.CTF_EVENT_REPORT({ userId, groupName }),
      queryFn: async () => {
        if (!userId) return null
        const accessToken = await getAccessTokenSilently()
        return await getCtfEventReport(accessToken, userId, { groupName })
      },
      enabled: !!(userId && groupName && enabled),
      staleTime: 60000,
      retry: (failureCount, error) => failureCount < 2 && !(error?.status >= 400 && error?.status < 500),
    })
  },
  CTF_LEADERBOARD: ({ groupName, enabled = true }) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.CTF_LEADERBOARD({ userId: user?.sub, groupName }),
      queryFn: async () => {
        if (user?.sub && groupName) {
          const accessToken = await getAccessTokenSilently()
          pageLogger.debug({ userId: user.sub, groupName }, "Fetching CTF leaderboard")
          return await getCtfLeaderboard(accessToken, user.sub, { groupName })
        }
        return null
      },
      enabled: !!(user?.sub && groupName && enabled),
      // staleTime 0: the leaderboard is only shown at GAME_OVER, when the query first becomes
      // enabled. A non-zero staleTime served the last cached mid-game total (e.g. 722 while the
      // true final was 1555) on first render. With 0 the query refetches on mount/enable so the
      // overlay opens on the final standings; refetchInterval still keeps it live afterward.
      staleTime: 0,
      // Stop polling once the server says the board is off for this exercise (nothing will change).
      refetchInterval: (query) => (query.state.data?.disabled ? false : 15000),
      refetchIntervalInBackground: false,
      // A 4xx (not authorised, unknown group) won't fix itself on retry; only retry transient failures.
      retry: (failureCount, error) => failureCount < 2 && !(error?.status >= 400 && error?.status < 500),
    })
  },
  ORG_IR_POLICYS: ({ orgId, enabled = true } = {}) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.ORG_IR_POLICYS({ userId: user?.sub, orgId }),
      queryFn: async () => {
        if (user?.sub && orgId) {
          const accessToken = await getAccessTokenSilently()
          return await fetchOrgIRPolicys(accessToken, user.sub, { orgId })
        }
        return []
      },
      enabled: !!(user?.sub && orgId && enabled),
      staleTime: 30000,
    })
  },
  // Incidents — paginated list scoped to the caller.
  INCIDENTS: ({ limit, skip, enabled = true } = {}) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.INCIDENTS({ userId: user?.sub, limit, skip }),
      queryFn: async () => {
        if (user?.sub) {
          const accessToken = await getAccessTokenSilently()
          return await getIncidents(accessToken, user.sub, { limit, skip })
        }
      },
      enabled: !!(user?.sub && enabled),
      staleTime: 30000,
    })
  },
  INCIDENT_VIEW: ({ incidentKey, enabled = true } = {}) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.INCIDENT_VIEW({ userId: user?.sub, incidentKey }),
      queryFn: async () => {
        if (user?.sub && incidentKey) {
          const accessToken = await getAccessTokenSilently()
          const { result } = await getIncidentView(accessToken, user.sub, { incidentKey })
          return result
        }
      },
      enabled: !!(user?.sub && incidentKey && enabled),
      staleTime: 30000,
    })
  },
  INCIDENT_AUDIT: ({ incidentKey, limit, beforeSeq, enabled = true } = {}) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.INCIDENT_AUDIT({ userId: user?.sub, incidentKey, limit, beforeSeq }),
      queryFn: async () => {
        if (user?.sub && incidentKey) {
          const accessToken = await getAccessTokenSilently()
          const { result } = await getIncidentAuditView(accessToken, user.sub, { incidentKey, limit, beforeSeq })
          return result
        }
      },
      enabled: !!(user?.sub && incidentKey && enabled),
      staleTime: 30000,
    })
  },
  // M4.5: member-scoped audit feed (mirrors INCIDENT_AUDIT). All roles may call
  // this — admins get the full unredacted set, USER/TRAINER get the redacted,
  // channel-scoped view from the same endpoint.
  INCIDENT_AUDIT_MEMBER: ({ incidentKey, limit, beforeSeq, enabled = true } = {}) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.INCIDENT_AUDIT_MEMBER({ userId: user?.sub, incidentKey, limit, beforeSeq }),
      queryFn: async () => {
        if (user?.sub && incidentKey) {
          const accessToken = await getAccessTokenSilently()
          const { result } = await getIncidentAuditForMember(accessToken, user.sub, { incidentKey, limit, beforeSeq })
          return result
        }
      },
      enabled: !!(user?.sub && incidentKey && enabled),
      staleTime: 30000,
    })
  },
  // M4.6: the caller's own incident notification feed for the header bell.
  // Polls every 60s in the foreground only (refetchIntervalInBackground:false)
  // so a backgrounded tab stops hammering the endpoint. Returns the raw
  // `{ notifications, unreadCount }` payload; the bell reads both off it.
  INCIDENT_NOTIFICATIONS: ({ enabled = true } = {}) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.INCIDENT_NOTIFICATIONS({ userId: user?.sub }),
      queryFn: async () => {
        if (user?.sub) {
          const accessToken = await getAccessTokenSilently()
          const { result } = await getIncidentNotifications(accessToken, user.sub, {})
          return result
        }
      },
      enabled: !!(user?.sub && enabled),
      staleTime: 30000,
      refetchInterval: 60000,
      refetchIntervalInBackground: false,
    })
  },
  // Lazy load user status per game - fetched on-demand when UserProfiles renders
  HOME_GAME_USER_STATUS: ({ gameId, emails, enabled = true }) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: cacheKeyGens.HOME_GAME_USER_STATUS({ userId: user?.sub, gameId, emails }),
      queryFn: async () => {
        if (user?.sub && emails && emails.length > 0) {
          const accessToken = await getAccessTokenSilently()
          pageLogger.debug({ userId: user.sub, gameId, emailCount: emails.length }, "Lazy loading user status for game")
          return await getUserData(accessToken, user.sub, { emails })
        }
        return []
      },
      enabled: !!(user?.sub && emails && emails.length > 0 && enabled),
      staleTime: 60000, // 1 minute - user status doesn't change often
      refetchInterval: false, // No auto-polling for user status
      retry: 2, // Retry failed requests
    })
  },
})

export const useCaches = Object.freeze({
  CLIENT_GLOBAL: () => {
    const queryClient = useQueryClient()
    return {
      clear: () => {
        if (queryClient) {
          queryClient.clear()
        }
      },
    }
  },
  SESSION_DATA: () => {
    const { user } = useAuth0()
    const queryClient = useQueryClient()
    return {
      setQueryData: ({ sessionId, updater, options }) => {
        if (queryClient && user?.sub) {
          const cacheKey = cacheKeyGens.SESSION_DATA({ userId: user.sub, sessionId })
          queryClient.setQueryData(cacheKey, updater, options)
        }
      },
    }
  },
})
